/**
 * PAPPO — GroupManageModal
 * Gerir um grupo DE UTILIZADOR: ver participantes, admin adiciona/remove
 * contatos, e QUALQUER membro pode sair. (O grupo "Família" não usa este modal —
 * é fechado.)
 */

import { useState, useEffect } from 'react';
import { LogOut, Plus, X } from 'lucide-react';
import type { Conversation } from '../../types/message';
import type { Contact } from '../../types/user';
import { listContacts } from '../../services/contacts';
import {
  addGroupMember,
  removeGroupMember,
  leaveGroup,
} from '../../services/groups';
import { Avatar } from '../common/Avatar';
import { Button } from '../common/Button';
import styles from './GroupModal.module.css';

interface GroupManageModalProps {
  group: Conversation;
  currentUserId: number;
  onClose: () => void;
  /** Grupo atualizado (após adicionar/remover). */
  onChanged: (group: Conversation) => void;
  /** O utilizador saiu — remover o grupo da lista. */
  onLeft: (groupId: number) => void;
}

export function GroupManageModal({
  group,
  currentUserId,
  onClose,
  onChanged,
  onLeft,
}: GroupManageModalProps) {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const isAdmin = !!group.is_admin;
  const participants = group.participants ?? [];
  const memberIds = new Set(participants.map((p) => p.id));

  useEffect(() => {
    if (!isAdmin) return;
    listContacts().then((res) => {
      if (res.success && res.data) {
        setContacts(
          res.data.contacts.filter(
            (c) => c.status === 'accepted' && c.contact_user?.id
          )
        );
      }
    });
  }, [isAdmin]);

  const addable = contacts.filter((c) => !memberIds.has(c.contact_user!.id));

  const handleAdd = async (userId: number) => {
    setBusy(true);
    setError('');
    const res = await addGroupMember(group.id, userId);
    setBusy(false);
    if (res.success && res.data?.group) onChanged(res.data.group);
    else setError(res.error || 'Não foi possível adicionar.');
  };

  const handleRemove = async (userId: number) => {
    setBusy(true);
    setError('');
    const res = await removeGroupMember(group.id, userId);
    setBusy(false);
    if (res.success) {
      onChanged({
        ...group,
        participants: participants.filter((p) => p.id !== userId),
      });
    } else {
      setError(res.error || 'Não foi possível remover.');
    }
  };

  const handleLeave = async () => {
    setBusy(true);
    setError('');
    const res = await leaveGroup(group.id);
    setBusy(false);
    if (res.success) {
      onLeft(group.id);
      onClose();
    } else {
      setError(res.error || 'Não foi possível sair.');
    }
  };

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.card} onClick={(e) => e.stopPropagation()}>
        <div className={styles.head}>
          <span className={styles.title}>{group.name || 'Grupo'}</span>
          <button className={styles.iconBtn} onClick={onClose} aria-label="Fechar">
            <X size={18} />
          </button>
        </div>

        <div className={styles.body}>
          <div className={styles.sectionLabel}>
            <span>Participantes</span>
            <span>{participants.length}</span>
          </div>
          <ul className={styles.list}>
            {participants.map((p) => (
              <li key={p.id}>
                <div className={styles.row} style={{ cursor: 'default' }}>
                  <Avatar name={p.display_name} src={p.avatar_url} size="sm" />
                  <div className={styles.rowInfo}>
                    <span className={styles.rowName}>
                      {p.display_name}
                      {p.id === currentUserId ? ' (você)' : ''}
                    </span>
                    <span className={styles.rowMeta}>
                      {p.role === 'child' ? 'Menor' : 'Responsável'}
                    </span>
                  </div>
                  {p.is_admin && <span className={styles.badge}>Admin</span>}
                  {isAdmin && p.id !== currentUserId && (
                    <button
                      type="button"
                      className={styles.removeBtn}
                      onClick={() => handleRemove(p.id)}
                      disabled={busy}
                    >
                      Remover
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>

          {isAdmin && addable.length > 0 && (
            <>
              <div className={styles.sectionLabel}>
                <span>Adicionar contato</span>
              </div>
              <ul className={styles.list}>
                {addable.map((c) => {
                  const u = c.contact_user!;
                  return (
                    <li key={c.id}>
                      <button
                        type="button"
                        className={styles.row}
                        onClick={() => handleAdd(u.id)}
                        disabled={busy}
                      >
                        <Avatar name={u.display_name} src={u.avatar_url} size="sm" />
                        <div className={styles.rowInfo}>
                          <span className={styles.rowName}>{u.display_name}</span>
                          <span className={styles.rowMeta}>
                            {u.role === 'child' ? 'Menor' : 'Responsável'}
                          </span>
                        </div>
                        <span className={styles.check}>
                          <Plus size={14} />
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </>
          )}

          {error && <p className={styles.error}>{error}</p>}
        </div>

        <div className={styles.leaveRow}>
          <Button
            variant="danger"
            onClick={handleLeave}
            isLoading={busy}
            leftIcon={<LogOut size={16} />}
            fullWidth
          >
            Sair do grupo
          </Button>
        </div>
      </div>
    </div>
  );
}
